import { Download, ExternalLink, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { AddFileDialog } from "./AddFileDialog";
import { AddLinkDialog } from "./AddLinkDialog";
import { downloadUrl, useDeleteFile, useFiles, type FileOut } from "@/api/hooks/files";
import { errorMessage } from "@/api/errors";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { EmptyState } from "@/components/EmptyState";
import { ErrorNotice } from "@/components/ErrorNotice";
import { fileKind, FileTypeIcon } from "@/components/FileTypeIcon";
import { PageLoading } from "@/components/Spinner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TBody, TD, TH, THead, TR } from "@/components/ui/table";
import { canManage, useOrgRole, useProjectParams } from "@/features/projects/projectContext";
import { formatDate } from "@/lib/dates";

const iconButton =
  "inline-flex h-11 w-11 items-center justify-center rounded-full text-heading transition-colors hover:bg-secondary-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus";

export function FilesPage() {
  const { t } = useTranslation();
  const { org, project } = useProjectParams();
  const role = useOrgRole(org);
  const manage = canManage(role);
  const files = useFiles(org, project);
  const del = useDeleteFile(org, project);
  const [fileOpen, setFileOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [toDelete, setToDelete] = useState<FileOut | null>(null);

  return (
    <section aria-labelledby="files-title" className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 id="files-title">{t("files.title")}</h2>
          <p className="text-fg-muted">{t("files.intro")}</p>
        </div>
        {manage ? (
          <div className="flex flex-wrap gap-3">
            <Button variant="secondary" onClick={() => setLinkOpen(true)}>
              {t("files.addLink")}
            </Button>
            <Button onClick={() => setFileOpen(true)}>{t("files.addFile")}</Button>
          </div>
        ) : null}
      </div>

      {files.isPending ? <PageLoading /> : null}
      {files.isError ? <ErrorNotice error={files.error} /> : null}
      {files.data && files.data.length === 0 ? <EmptyState title={t("files.emptyTitle")} description={t("files.emptyBody")} /> : null}
      {files.data && files.data.length > 0 ? (
        <Table>
          <THead>
            <TR>
              <TH>{t("files.colName")}</TH>
              <TH>{t("files.colType")}</TH>
              <TH>{t("files.colAddedBy")}</TH>
              <TH>{t("files.colDate")}</TH>
              <TH className="text-right">
                <span className="sr-only">{t("files.colActions")}</span>
              </TH>
            </TR>
          </THead>
          <TBody>
            {files.data.map((f) => (
              <TR key={f.id}>
                <TD>
                  <div className="flex flex-col">
                    <span className="flex flex-wrap items-center gap-2 font-medium">
                      {f.title}
                      {f.visibility === "staff_only" ? <Badge variant="grey">{t("files.staffOnly")}</Badge> : null}
                    </span>
                    {f.description ? <span className="text-sm text-fg-muted">{f.description}</span> : null}
                  </div>
                </TD>
                <TD className="whitespace-nowrap text-fg-muted">
                  <FileTypeIcon kind={fileKind(f.kind, f.filename, f.content_type)} />
                </TD>
                <TD className="whitespace-nowrap">{f.uploaded_by_name}</TD>
                <TD className="whitespace-nowrap text-fg-muted">
                  <time dateTime={f.created_at}>{formatDate(f.created_at)}</time>
                </TD>
                <TD className="whitespace-nowrap text-right">
                  <a
                    className={iconButton}
                    href={downloadUrl(org, project, f.id)}
                    target={f.kind === "link" ? "_blank" : undefined}
                    rel={f.kind === "link" ? "noopener noreferrer" : undefined}
                    aria-label={f.kind === "link" ? t("files.open", { name: f.title }) : t("files.download", { name: f.title })}
                  >
                    {f.kind === "link" ? <ExternalLink aria-hidden="true" className="h-5 w-5" /> : <Download aria-hidden="true" className="h-5 w-5" />}
                  </a>
                  {manage ? (
                    <button type="button" className={iconButton} aria-label={t("files.delete", { name: f.title })} onClick={() => setToDelete(f)}>
                      <Trash2 aria-hidden="true" className="h-5 w-5" />
                    </button>
                  ) : null}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      ) : null}

      {manage ? (
        <>
          <AddFileDialog org={org} project={project} open={fileOpen} onOpenChange={setFileOpen} />
          <AddLinkDialog org={org} project={project} open={linkOpen} onOpenChange={setLinkOpen} />
          <ConfirmDialog
            open={toDelete !== null}
            onOpenChange={(o) => !o && setToDelete(null)}
            title={t("files.deleteConfirm", { name: toDelete?.title ?? "" })}
            description={t("files.deleteBody")}
            confirmLabel={t("files.deleteAction")}
            busy={del.isPending}
            onConfirm={() =>
              toDelete &&
              del.mutate(toDelete.id, {
                onSuccess: () => {
                  toast.success(t("files.deleted"));
                  setToDelete(null);
                },
                onError: (e) => toast.error(errorMessage(e, t("errors.generic"))),
              })
            }
          />
        </>
      ) : null}
    </section>
  );
}
